import { RUTERO_HOLD_OPTIONAL_ITEM_CODES_MAX } from '@bandido/shared/types';
import type { RuteroMoney } from '@bandido/shared/types';

/**
 * Los adicionales OPCIONALES que publica una oferta, y su reserva en el hold.
 *
 * Un adicional es lo que el proveedor vende ADEMÁS del servicio y fuera del
 * `price` de la oferta (ejemplo real del contrato, en un traslado: "30 minutos
 * adicionales de tiempo de espera", EUR 45,45, por reserva). Se reserva EN el
 * mismo hold: cada unidad elegida repite su `detailBookingCode` en
 * `options.optionalItemCodes` (#2383; el canal lo pidió #2333). En un ida y
 * vuelta cada tramo publica su propio catálogo con sus propios códigos —
 * pedir un servicio para la ida no lo pide para la vuelta.
 *
 * **No son los extras** (`bookingExtras.ts`): son mecanismos distintos, de
 * momentos distintos. El extra se compra sobre la reserva YA creada
 * (`POST /bookings/{id}/extras`, capability `extras` del conector) y por eso
 * vive en Reservado y en Confirmado. El adicional es parte de la OFERTA y se
 * elige ANTES de reservar.
 *
 * Acá no se suma plata NUNCA: la selección muestra los precios unitarios que
 * ya vienen en la oferta y el total con adicionales lo devuelve el HOLD — el
 * modal post-hold pinta el booking autoritativo (21.23).
 */

/** Tope de adicionales que se dibujan: lista del proveedor, acotada (21.11). */
const MAX_OPTIONAL_ITEMS = 20;
/** Mismo tope que le da el backend al nombre de una opción del proveedor. */
const MAX_NAME = 160;

export interface OfferOptionalItem {
  /**
   * Código nativo del adicional en el proveedor ("T12"). Es lo que viaja al
   * hub en `options.optionalItemCodes` (una entrada por unidad), así que se
   * conserva con el nombre del contrato.
   */
  detailBookingCode: string;
  name: string;
  price: RuteroMoney;
  /**
   * `true` = el precio es POR PERSONA. Ausente cuando la respuesta no lo
   * afirma: rotular "por reserva" por ausencia mentiría sobre un importe que
   * quizá se multiplica (mismo criterio que `pricePerPassenger` de la oferta).
   */
  perPerson?: boolean;
  /**
   * A qué TRAMO aplica en un ida y vuelta (`outbound`/`return`). Ausente = la
   * oferta tiene un solo tramo. El rótulo del tramo se dibuja solo cuando el
   * dato viene: nada que el contrato no traiga.
   */
  direction?: 'outbound' | 'return';
  /**
   * Unidades máximas que acepta el operador (0 es real: no acepta ninguna).
   * Ausente = el proveedor no publica límite; ahí rige solo el tope global
   * del hold. Pedir de más se rechaza con `OPTIONAL_ITEM_LIMIT`.
   */
  maxAllowed?: number;
}

/**
 * Un importe que se puede MOSTRAR, con el mismo criterio que el backend
 * (`optionalItemPrice`): un adicional PUEDE valer cero.
 *
 * El contrato no le pone mínimo a `amountMinor`, y un adicional sin cargo es un
 * caso real —el proveedor lo publica para que se sepa que existe—, así que
 * descartarlo escondería algo que sí ofrece. Negativos y no enteros no se
 * pueden mostrar sin inventar.
 */
function money(value: unknown): RuteroMoney | undefined {
  const candidate = value as RuteroMoney | undefined;
  if (!candidate || typeof candidate !== 'object') return undefined;
  if (!Number.isInteger(candidate.amountMinor) || candidate.amountMinor < 0) {
    return undefined;
  }
  if (
    typeof candidate.currency !== 'string' ||
    !/^[A-Za-z]{3}$/.test(candidate.currency)
  ) {
    return undefined;
  }
  return {
    amountMinor: candidate.amountMinor,
    currency: candidate.currency.toUpperCase(),
  };
}

function text(value: unknown, max: number): string {
  return typeof value === 'string' ? value.slice(0, max).trim() : '';
}

/**
 * Los adicionales dibujables (y reservables) de una oferta.
 *
 * El backend ya los sanea en el punto único de ingesta de ofertas
 * (`sanitizeOfferOptionalItems`); acá se vuelven a normalizar por lo mismo que
 * `conditions` y `requiredPassengerInfo`: al modal también le pueden llegar
 * respuestas cacheadas o de un backend anterior al saneo, y de ese `unknown`
 * sale directo lo que se pinta y lo que se manda al hold.
 *
 * Un adicional sin código, sin nombre o sin un importe mostrable se DESCARTA
 * entero: media fila —un nombre sin precio, o un precio sin saber de qué es—
 * es peor que no ofrecerlo, porque el asesor lo transmite igual. El código es
 * IDENTIDAD OPACA del proveedor y el contrato no le declara tope de largo:
 * viaja intacto (ni truncar ni descartar por longitud — un código mutilado
 * reservaría otra cosa o nada, y un tope local perdería en silencio un
 * adicional válido). `direction` solo con los dos valores del contrato;
 * `maxAllowed` solo como entero ≥ 0.
 */
export function offerOptionalItems(value: unknown): OfferOptionalItem[] {
  if (!Array.isArray(value)) return [];
  const items: OfferOptionalItem[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const raw = entry as Record<string, unknown>;
    const detailBookingCode =
      typeof raw.detailBookingCode === 'string'
        ? raw.detailBookingCode.trim()
        : '';
    const name = text(raw.name, MAX_NAME);
    const price = money(raw.price);
    if (!detailBookingCode || !name || !price) continue;
    items.push({
      detailBookingCode,
      name,
      price,
      ...(typeof raw.perPerson === 'boolean' ? { perPerson: raw.perPerson } : {}),
      ...(raw.direction === 'outbound' || raw.direction === 'return'
        ? { direction: raw.direction }
        : {}),
      ...(typeof raw.maxAllowed === 'number' &&
      Number.isInteger(raw.maxAllowed) &&
      raw.maxAllowed >= 0
        ? { maxAllowed: raw.maxAllowed }
        : {}),
    });
    if (items.length === MAX_OPTIONAL_ITEMS) break;
  }
  return items;
}

/**
 * Si la sección existe: hay adicionales dibujables Y la pantalla es la del
 * asesor.
 *
 * Los importes son del PROVEEDOR y no llevan el markup de la agencia (donde el
 * conector es `net`, son netos). Es la MISMA razón por la que el precio
 * congelado del proveedor no se le muestra a la audiencia minorista
 * (`resolveBookingPrice`): el pasajero leería como suyo un número que no es el
 * que va a pagar, y de paso el neto de la agencia.
 */
export function optionalItemsSectionVisible({
  items,
  isAgent,
}: {
  items: readonly OfferOptionalItem[];
  isAgent: boolean;
}): boolean {
  return isAgent && items.length > 0;
}

/**
 * La selección del asesor: unidades por `detailBookingCode`. Solo códigos con
 * unidades > 0 tienen entrada — el estado vacío es `{}`.
 */
export type OptionalItemSelection = Readonly<Record<string, number>>;

/** Unidades elegidas de un adicional (0 si no tiene entrada). */
export function optionalItemUnits(
  selection: OptionalItemSelection,
  detailBookingCode: string,
): number {
  const units = selection[detailBookingCode];
  return Number.isInteger(units) && (units as number) > 0 ? (units as number) : 0;
}

/** Unidades totales de la selección (lo que acota el tope global del hold). */
export function totalOptionalItemUnits(selection: OptionalItemSelection): number {
  return Object.keys(selection).reduce(
    (sum, code) => sum + optionalItemUnits(selection, code),
    0,
  );
}

/**
 * Si se puede sumar UNA unidad más de este adicional. Es la MISMA expresión
 * que aplica `changeOptionalItemUnits` (21.9: lo que se valida y lo que se
 * dibuja salen de una sola derivación): el botón de "+" se deshabilita
 * exactamente cuando el cambio no haría nada.
 *
 * Dos topes, los dos del contrato: el `maxAllowed` del item (puede ser 0) y
 * el `maxItems` global de `options.optionalItemCodes`.
 */
export function canAddOptionalItemUnit({
  selection,
  item,
}: {
  selection: OptionalItemSelection;
  item: OfferOptionalItem;
}): boolean {
  if (totalOptionalItemUnits(selection) >= RUTERO_HOLD_OPTIONAL_ITEM_CODES_MAX) {
    return false;
  }
  const units = optionalItemUnits(selection, item.detailBookingCode);
  return item.maxAllowed === undefined || units < item.maxAllowed;
}

/**
 * El ÚNICO mutador de la selección: aplica el delta respetando los topes, así
 * que un estado que exceda `maxAllowed` o el tope global es inalcanzable desde
 * la UI (no hace falta truncar nada después, que estaría prohibido — 21.22.5).
 * Un código que no es de esta oferta no crea entrada.
 */
export function changeOptionalItemUnits(
  selection: OptionalItemSelection,
  items: readonly OfferOptionalItem[],
  detailBookingCode: string,
  delta: 1 | -1,
): OptionalItemSelection {
  const item = items.find(
    (candidate) => candidate.detailBookingCode === detailBookingCode,
  );
  if (!item) return selection;
  if (delta > 0 && !canAddOptionalItemUnit({ selection, item })) return selection;
  const next = optionalItemUnits(selection, detailBookingCode) + delta;
  const updated: Record<string, number> = { ...selection };
  delete updated[detailBookingCode];
  return next > 0 ? { ...updated, [detailBookingCode]: next } : updated;
}

/**
 * La selección tal como VIAJA en el hold: una entrada POR UNIDAD, en el orden
 * en que la oferta publicó los adicionales (repetir un código = más unidades,
 * mismo criterio que `accommodationIds`).
 *
 * Se recorre `items` y no la selección para que un código que no es de ESTA
 * oferta (estado viejo de otra búsqueda) jamás viaje: mandarlo sería un
 * `UNKNOWN_OFFER_OPTIONS` seguro. Los topes ya los garantizó el mutador.
 */
export function optionalItemCodesForHold(
  selection: OptionalItemSelection,
  items: readonly OfferOptionalItem[],
): string[] {
  const codes: string[] = [];
  for (const item of items) {
    const units = optionalItemUnits(selection, item.detailBookingCode);
    for (let unit = 0; unit < units; unit += 1) {
      codes.push(item.detailBookingCode);
    }
  }
  return codes;
}

export interface SelectedOptionalItemSummary {
  name: string;
  quantity: number;
}

/**
 * Nombres y cantidades que explican el suplemento cotizado por el backend.
 * No calcula importes: el monto agregado sigue siendo exclusivamente
 * `preview.supplement.final`; esta derivación solo evita el rótulo opaco
 * "opciones" cuando sabemos exactamente qué eligió el asesor.
 */
export function selectedOptionalItemsSummary(
  selection: OptionalItemSelection,
  items: readonly OfferOptionalItem[],
): SelectedOptionalItemSummary[] {
  return items.flatMap((item) => {
    const quantity = optionalItemUnits(selection, item.detailBookingCode);
    return quantity > 0 ? [{ name: item.name, quantity }] : [];
  });
}

/**
 * Los adicionales agrupados por tramo, en el orden en que la oferta los
 * publicó. El rótulo del tramo (Ida/Vuelta) se dibuja solo cuando el item lo
 * declara — `null` agrupa los de oferta de un solo tramo, sin rótulo.
 */
export function optionalItemsByDirection(
  items: readonly OfferOptionalItem[],
): [direction: 'outbound' | 'return' | null, items: OfferOptionalItem[]][] {
  const groups: [
    'outbound' | 'return' | null,
    OfferOptionalItem[],
  ][] = [];
  for (const item of items) {
    const direction = item.direction ?? null;
    const group = groups.find(([key]) => key === direction);
    if (group) group[1].push(item);
    else groups.push([direction, [item]]);
  }
  return groups;
}

/**
 * El rechazo del hold que pertenece a ESTA sección, resuelto contra el payload
 * QUE SE MANDÓ (no contra el estado actual del form — 21.23.4).
 *
 * - `RUTERO_HOLD_OPTIONAL_ITEM_LIMIT` solo puede referirse a adicionales, pero
 *   igual se exige que el envío llevara códigos: sin ellos la sección puede ni
 *   estar dibujada, y un error apuntando a una sección ausente es un error
 *   invisible — ahí queda el toast genérico.
 * - `RUTERO_HOLD_UNKNOWN_OFFER_OPTIONS` también nombra acomodaciones/tarifas:
 *   es de esta sección solo si algún `unknownId` es un código de adicional que
 *   viajó. Los demás siguen su camino de siempre.
 *
 * Devuelve los items rechazados por NOMBRE cuando el catálogo de la oferta
 * todavía los tiene (lo único legible para el asesor); el código queda de
 * fallback.
 */
export type OptionalItemsHoldError =
  | { kind: 'unknown'; names: string[] }
  | { kind: 'limit'; message?: string };

export function optionalItemsHoldError({
  code,
  message,
  unknownIds,
  sentCodes,
  items,
}: {
  code: string | undefined;
  message: string | undefined;
  unknownIds: readonly string[];
  sentCodes: readonly string[];
  items: readonly OfferOptionalItem[];
}): OptionalItemsHoldError | null {
  if (!sentCodes.length) return null;
  if (code === 'RUTERO_HOLD_OPTIONAL_ITEM_LIMIT') {
    return { kind: 'limit', ...(message?.trim() ? { message: message.trim() } : {}) };
  }
  if (code !== 'RUTERO_HOLD_UNKNOWN_OFFER_OPTIONS') return null;
  const sent = new Set(sentCodes);
  const rejected = unknownIds.filter((id) => sent.has(id));
  if (!rejected.length) return null;
  const names = [...new Set(rejected)].map(
    (rejectedCode) =>
      items.find((item) => item.detailBookingCode === rejectedCode)?.name ??
      rejectedCode,
  );
  return { kind: 'unknown', names };
}
